import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { returnsAPI, salesOrderAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const ReturnForm = ({ salesOrderId: propSalesOrderId }) => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const params = useParams();
  const returnId = params.id;
  const urlSalesOrderId = params.salesOrderId;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [salesOrder, setSalesOrder] = useState(null);
  const [returnData, setReturnData] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [returnReason, setReturnReason] = useState('Defective');
  const [refundMethod, setRefundMethod] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [availableQuantities, setAvailableQuantities] = useState({});

  const salesOrderId = returnId ? null : (urlSalesOrderId || propSalesOrderId);

  useEffect(() => {
    if (returnId) {
      loadExistingReturn();
    } else if (salesOrderId) {
      loadSalesOrder();
    }
  }, [returnId, salesOrderId]);

  const loadSalesOrder = async () => {
    setLoading(true);
    try {
      const response = await salesOrderAPI.getOne(salesOrderId);
      setSalesOrder(response.data.data);

      const initialItems = {};
      if (response.data.data.items && Array.isArray(response.data.data.items)) {
        response.data.data.items.forEach(item => {
          initialItems[item.id] = {
            quantity: 0,
            condition: 'Used',
            reason: '',
          };
        });
      }
      setSelectedItems(initialItems);

      // Load available quantities for return
      try {
        const availableResponse = await fetch(`/api/returns/available-quantities/${salesOrderId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (availableResponse.ok) {
          const availableData = await availableResponse.json();
          const availableMap = {};
          (availableData.data || []).forEach(item => {
            availableMap[item.sales_order_item_id] = item;
          });
          setAvailableQuantities(availableMap);
        }
      } catch (error) {
        console.error('Error loading available quantities:', error);
        // Continue without available quantities
      }
    } catch (error) {
      console.error('Error loading sales order:', error);
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const loadExistingReturn = async () => {
    setLoading(true);
    try {
      const response = await returnsAPI.getOne(returnId);
      setReturnData(response.data.data);
      setSalesOrder(response.data.data.sales_order);
      setReturnReason(response.data.data.return_reason || 'Defective');
      setRefundMethod(response.data.data.refund_method || 'Cash');
      setNotes(response.data.data.notes || '');
    } catch (error) {
      console.error('Error loading return:', error);
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (itemId, value) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: parseFloat(value) || 0,
      }
    }));
  };

  const handleConditionChange = (itemId, value) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        condition: value,
      }
    }));
  };

  const handleReasonChange = (itemId, value) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        reason: value,
      }
    }));
  };

  const calculateRefundSummary = () => {
    if (!salesOrder) {
      return { items: 0, vat: 0, discount: 0, total: 0 };
    }

    let itemsRefund = 0;
    let discountRefund = 0;

    if (salesOrder.items && Array.isArray(salesOrder.items)) {
      salesOrder.items.forEach(item => {
        const selected = selectedItems[item.id];
        if (selected && selected.quantity > 0) {
          const returnedSubtotal = selected.quantity * parseFloat(item.unit_price);
          const proportionReturned = selected.quantity / parseFloat(item.quantity);
          const itemDiscount = parseFloat(item.discount_amount || 0);

          itemsRefund += returnedSubtotal - (proportionReturned * itemDiscount);
          discountRefund += proportionReturned * itemDiscount;
        }
      });
    }

    const totalPrice = parseFloat(salesOrder.total_price || 0);
    const vatAmount = parseFloat(salesOrder.vat_amount || 0);
    const feeAmount = parseFloat(salesOrder.fee_amount || 0);
    const discountAmount = parseFloat(salesOrder.discount_amount || 0);

    const denominator = totalPrice - vatAmount - feeAmount + discountAmount;
    const refundRatio = denominator > 0 ? itemsRefund / denominator : 0;
    const vatRefund = refundRatio * vatAmount;

    return {
      items: itemsRefund,
      discount: discountRefund,
      vat: vatRefund,
      total: itemsRefund + vatRefund,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const returnItems = [];
      if (salesOrder.items && Array.isArray(salesOrder.items)) {
        salesOrder.items.forEach(item => {
          const selected = selectedItems[item.id];
          if (selected && selected.quantity > 0) {
            returnItems.push({
              sales_order_item_id: item.id,
              inventory_item_id: item.inventory_item_id,
              returned_quantity: selected.quantity,
              condition: selected.condition,
              reason: selected.reason,
            });
          }
        });
      }

      if (returnItems.length === 0) {
        showWarning(t('returns.selectAtLeastOneItem'));
        setSaving(false);
        return;
      }

      const payload = {
        sales_order_id: salesOrder.id,
        return_reason: returnReason,
        refund_method: refundMethod,
        notes,
        items: returnItems,
      };

      if (returnData && returnData.status === 'Pending') {
        await returnsAPI.update(returnData.id, {
          return_reason: returnReason,
          refund_method: refundMethod,
          notes,
        });
      } else {
        await returnsAPI.create(payload);
      }

      showSuccess(t('messages.saveSuccess'));
      navigate('/returns');
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleProcess = async () => {
    if (!window.confirm(t('returns.confirmProcess'))) return;

    setSaving(true);
    try {
      await returnsAPI.process(returnData.id);
      showSuccess(t('returns.processedSuccess'));
      navigate('/returns');
    } catch (error) {
      showError(error.response?.data?.message || t('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const summary = calculateRefundSummary();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!salesOrder) {
    return <div className="text-center text-red-600 py-16">{t('messages.error')}</div>;
  }

  const isReadOnly = returnData && returnData.status !== 'Pending';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
              {returnData ? t('returns.editReturn') : t('returns.createReturn')}
            </h1>
            <p className="text-gray-600 mt-2">
              {t('salesOrders.orderNumber')}: <span className="font-semibold">{salesOrder.order_number}</span>
            </p>
          </div>
          <Link
            to="/returns"
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Return Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <div className="w-1 h-6 bg-red-600 rounded"></div>
              {t('returns.returnInformation')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('returns.reason')}
                </label>
                <select
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all disabled:bg-gray-100"
                >
                  <option value="Defective">{t('returns.defective')}</option>
                  <option value="Wrong Item">{t('returns.wrongItem')}</option>
                  <option value="Not Needed">{t('returns.notNeeded')}</option>
                  <option value="Changed Mind">{t('returns.changedMind')}</option>
                  <option value="Damaged">{t('returns.damaged')}</option>
                  <option value="Other">{t('returns.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('returns.refundMethod')}
                </label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all disabled:bg-gray-100"
                >
                  <option value="Cash">{t('returns.cash')}</option>
                  <option value="Credit">{t('returns.credit')}</option>
                  <option value="Check">{t('returns.check')}</option>
                  <option value="Bank Transfer">{t('returns.bankTransfer')}</option>
                </select>
              </div>

              {returnData && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('common.status')}
                  </label>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="px-3 py-1 text-sm font-bold rounded-full bg-yellow-100 text-yellow-800">
                      {returnData.status}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('common.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isReadOnly}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all disabled:bg-gray-100"
                placeholder={t('returns.notesPlaceholder')}
              />
            </div>
          </div>

          {/* Items Selection Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <div className="w-1 h-6 bg-red-600 rounded"></div>
              {t('returns.selectItems')}
            </h2>

            <div className="space-y-4">
              {salesOrder.items && salesOrder.items.map(item => {
                const available = availableQuantities[item.id];
                const maxAvailable = available?.available_quantity || item.quantity;
                const alreadyReturned = available?.already_returned || 0;
                const isFullyReturned = maxAvailable === 0;

                return (
                <div key={item.id} className={`border ${isFullyReturned ? 'border-gray-300 bg-gray-50' : 'border-gray-200 hover:border-red-200 hover:bg-red-50/30'} rounded-lg p-4 transition-all`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        {item.inventory_item?.item_name}
                        {isFullyReturned && (
                          <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 rounded-full">
                            {t('returns.fullyReturned')}
                          </span>
                        )}
                      </h3>
                      <div className="flex flex-col gap-2 text-sm text-gray-600">
                        <span>{t('salesOrders.orderQuantity')}: <span className="font-semibold">{parseFloat(item.quantity)}</span> {item.inventory_item?.unit}</span>
                        <span>{t('common.currency')} <span className="font-semibold">{parseFloat(item.unit_price).toFixed(2)}</span> / {item.inventory_item?.unit}</span>
                        {alreadyReturned > 0 && (
                          <span className="text-red-600">{t('returns.alreadyReturned')}: <span className="font-semibold">{alreadyReturned}</span> {item.inventory_item?.unit}</span>
                        )}
                        {maxAvailable > 0 && (
                          <span className="text-green-600">{t('returns.availableToReturn')}: <span className="font-semibold">{maxAvailable}</span> {item.inventory_item?.unit}</span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          {t('returns.returnQuantity')}
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={maxAvailable}
                          step="0.01"
                          value={selectedItems[item.id]?.quantity || 0}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          disabled={isReadOnly || isFullyReturned}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent ${isReadOnly || isFullyReturned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          {t('returns.condition')}
                        </label>
                        <select
                          value={selectedItems[item.id]?.condition || 'Used'}
                          onChange={(e) => handleConditionChange(item.id, e.target.value)}
                          disabled={isReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="Used">{t('returns.used')}</option>
                          <option value="Unopened">{t('returns.unopened')}</option>
                          <option value="Damaged">{t('returns.damaged')}</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {selectedItems[item.id]?.quantity > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        {t('returns.itemReason')}
                      </label>
                      <input
                        type="text"
                        value={selectedItems[item.id]?.reason || ''}
                        onChange={(e) => handleReasonChange(item.id, e.target.value)}
                        disabled={isReadOnly}
                        placeholder={t('returns.reasonPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100"
                      />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Refund Summary Card */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200 p-8">
            <h2 className="text-xl font-bold text-red-900 mb-6 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.16 2.75a.75.75 0 00-1.32 0l-3.5 6A.75.75 0 003.75 10h12.5a.75.75 0 00.66-1.25l-3.5-6z" />
              </svg>
              {t('returns.refundSummary')}
            </h2>

            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-red-900">{t('returns.itemsRefund')}:</span>
                <span className="text-lg font-bold text-red-700">
                  {t('common.currency')} {summary.items.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-red-900">{t('returns.discountRefund')}:</span>
                <span className="text-lg font-bold text-red-700">
                  {t('common.currency')} {summary.discount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm pb-3 border-b-2 border-red-200">
                <span className="text-red-900">{t('returns.vatRefund')}:</span>
                <span className="text-lg font-bold text-red-700">
                  {t('common.currency')} {summary.vat.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-3">
                <span className="text-lg font-bold text-red-900">{t('returns.totalRefund')}:</span>
                <span className="text-3xl font-bold text-red-600">
                  {t('common.currency')} {summary.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 justify-between">
            <div className="flex gap-3">
              {!isReadOnly && (
                <>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:shadow-lg transition-all font-semibold disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {saving ? t('common.saving') : t('returns.createReturn')}
                  </button>
                  <Link
                    to="/returns"
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all font-semibold"
                  >
                    {t('common.cancel')}
                  </Link>
                </>
              )}
            </div>

            {returnData && returnData.status === 'Pending' && (
              <button
                type="button"
                onClick={handleProcess}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:shadow-lg transition-all font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('returns.processReturn')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReturnForm;
